/**
 * Khoản chi và tiền lời (KH-GĐ2 5.3, công tắc "Khoản chi và tiền lời"):
 *   - `TheChiTieu`: Sổ tiền → Chi tiêu — tiền lời tháng (một số lớn) + "Ghi khoản chi" + các khoản chi của tháng;
 *   - `NutHoaHongThang`: thẻ Bảng hoa hồng — ghi tổng bảng đã chốt thành một khoản chi.
 * Mọi con số do MÁY CHỦ tính; chi tiền mặt thì két trừ đi (máy chủ).
 */
import { laNhomChiCoSan, NHOM_CHI_CO_SAN, type NhomChiCoSan } from '@sapa/shared'
import { ChevronLeft, ChevronRight, Plus, Wallet, X } from 'lucide-react'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { type Api, type KhoanChi, ngayVn, type TienLoi, type ToiLaAi, tienVnd } from './api.ts'
import { useChiNhanhCuaTo } from './chi-nhanh-dang-dung.tsx'
import { loiCua, lucViet, OLyDo, OTien, useKhoaIdem } from './GoiVi.tsx'
import { C, NHOM_CHI_TEN } from './text/khoan-chi.ts'
import { T } from './text/vi.ts'
import { ThongBao, To, Xuong } from './ui.tsx'

/** "2026-08" ± n tháng. */
export function doiThang(thang: string, n: number): string {
  const nam = Number.parseInt(thang.slice(0, 4), 10)
  const t = Number.parseInt(thang.slice(5, 7), 10) - 1 + n
  const nam2 = nam + Math.floor(t / 12)
  const t2 = ((t % 12) + 12) % 12
  return `${nam2}-${String(t2 + 1).padStart(2, '0')}`
}

const nhanThang = (thang: string) => `${thang.slice(5)}/${thang.slice(0, 4)}`

function tenNhom(nhom: string | null, tenRieng: string | null): string {
  if (tenRieng !== null) return tenRieng
  return laNhomChiCoSan(nhom) ? NHOM_CHI_TEN[nhom] : (nhom ?? '')
}

export function TheChiTieu({ api, toi }: { api: Api; toi: ToiLaAi }) {
  const xemLoi = toi.quyen.includes('xem.doanh-thu')
  const choDao = toi.quyen.includes('hoan-dao-tien')
  const thangNay = ngayVn().slice(0, 7)
  const [thang, setThang] = useState(thangNay)
  const [tl, setTl] = useState<TienLoi | null>(null)
  const [mo, setMo] = useState(false)
  const [bao, setBao] = useState<string | null>(null)
  const [loi, setLoi] = useState<string | null>(null)

  const nap = useCallback(() => {
    if (!xemLoi) return
    setLoi(null)
    void api
      .tienLoi(thang)
      .then(setTl)
      .catch((e: unknown) => setLoi(loiCua(e)))
  }, [api, thang, xemLoi])
  useEffect(() => {
    setTl(null)
    nap()
  }, [nap])

  return (
    <>
      {bao !== null && (
        <ThongBao loai="thanh-cong" onDong={() => setBao(null)}>
          {bao}
        </ThongBao>
      )}
      {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
      {xemLoi && (
        <div className="dieu-huong-ngay">
          <button
            type="button"
            className="nut nut-icon"
            aria-label={C.thangTruoc}
            onClick={() => setThang(doiThang(thang, -1))}
          >
            <ChevronLeft size={22} />
          </button>
          <span className="nhan-ngay">{C.nhanThang(nhanThang(thang))}</span>
          <button
            type="button"
            className="nut nut-icon"
            aria-label={C.thangSau}
            disabled={thang >= thangNay}
            onClick={() => setThang(doiThang(thang, 1))}
          >
            <ChevronRight size={22} />
          </button>
        </div>
      )}
      {xemLoi &&
        (tl === null ? loi === null && <Xuong cao={120} /> : <TheTienLoi tl={tl} thang={thang} />)}
      <button
        type="button"
        className="nut nut-chinh nut-lon"
        style={{ width: '100%', marginTop: 12 }}
        onClick={() => setMo(true)}
      >
        <Wallet size={20} />
        {C.ghi}
      </button>
      {xemLoi && tl !== null && (
        <DsKhoanChi
          api={api}
          ds={tl.khoanChi}
          choDao={choDao}
          onDaBo={() => {
            setBao(C.daBoXong)
            nap()
          }}
        />
      )}
      {mo && (
        <ToGhiChi
          api={api}
          choHoan={choDao}
          choThemNhom={toi.quyen.includes('cau-hinh.nhan-vien')}
          onDong={() => setMo(false)}
          onXong={(cau) => {
            setMo(false)
            setBao(cau)
            nap()
          }}
        />
      )}
    </>
  )
}

function TheTienLoi({ tl, thang }: { tl: TienLoi; thang: string }) {
  const loi = BigInt(tl.loiVnd)
  return (
    <section className="the the-dem so-to">
      <span className="nhan-muc">
        {loi < 0n ? C.lo(nhanThang(thang)) : C.loiThang(nhanThang(thang))}
      </span>
      <b className={loi < 0n ? 'thieu-tien' : undefined}>
        {loi < 0n ? `−${tienVnd((-loi).toString())}` : tienVnd(tl.loiVnd)}
      </b>
      {/* Dòng giải thích chính của con số: cỡ chữ thường, không phải chú thích (ui-critic v30). */}
      <span style={{ fontSize: 16 }}>
        {C.thuTruChi(
          tienVnd(tl.thuVnd),
          tl.traLaiVnd === '0' ? null : tienVnd(tl.traLaiVnd),
          tienVnd(tl.chiVnd),
        )}
      </span>
      {tl.traTruocVnd !== '0' && (
        <span className="chu-phu">{C.traTruoc(tienVnd(tl.traTruocVnd))}</span>
      )}
      <span className="chu-phu">{C.giaiThich}</span>
    </section>
  )
}

/** Các khoản chi của tháng; "Ghi nhầm" hỏi lại NGAY dưới dòng vừa chạm, kèm lý do (vào nhật ký). */
function DsKhoanChi({
  api,
  ds,
  choDao,
  onDaBo,
}: {
  api: Api
  ds: KhoanChi[]
  choDao: boolean
  onDaBo: () => void
}) {
  const [hoi, setHoi] = useState<string | null>(null)
  const [lyDo, setLyDo] = useState('')
  const [dang, setDang] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  // Hộp hỏi hiện ra thì cuộn tới để thấy nút (ui-critic v30: hộp nằm ngoài màn hình).
  useEffect(() => {
    if (hoi !== null) document.getElementById('hoi-bo-khoan')?.scrollIntoView({ block: 'center' })
  }, [hoi])

  async function bo(id: string) {
    setDang(true)
    setLoi(null)
    try {
      await api.daoKhoanChi(id, lyDo.trim(), khoa)
      lamMoiKhoa()
      setHoi(null)
      setLyDo('')
      onDaBo()
    } catch (e) {
      setLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }

  return (
    <section className="the the-dem so-to">
      <span className="nhan-muc">{C.dsTitle}</span>
      {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
      {ds.length === 0 ? (
        <p className="chu-phu">{C.chuaCo}</p>
      ) : (
        <ul className="ds-so">
          {ds.map((k) => (
            <Fragment key={k.id}>
              <li>
                <span>
                  {k.laDao ? C.dongBo(k.ngayGoc) : tenNhom(k.nhom, k.tenNhom)}
                  <span className="chu-phu" style={{ display: 'block' }}>
                    {lucViet(k.luc)} · {k.cach === 'cash' ? C.tienMatNgan : C.chuyenKhoan}
                    {C.dongNguoi(k.nguoi)}
                    {k.note !== null && ` · ${k.note}`}
                    {k.daDao && ` · ${C.daBo}`}
                  </span>
                  {choDao && !k.laDao && !k.daDao && hoi !== k.id && (
                    <button type="button" className="nut-lien-ket" onClick={() => setHoi(k.id)}>
                      {C.boKhoan}
                    </button>
                  )}
                </span>
                <b className={k.daDao ? 'chu-phu' : undefined}>
                  {k.laDao ? `−${tienVnd(k.soTienVnd.replace('-', ''))}` : tienVnd(k.soTienVnd)}
                </b>
              </li>
              {hoi === k.id && (
                <li id="hoi-bo-khoan" style={{ display: 'block' }}>
                  <ThongBao loai="canh-bao">
                    {C.boHoi(tenNhom(k.nhom, k.tenNhom), tienVnd(k.soTienVnd), k.cach === 'cash')}
                  </ThongBao>
                  <OLyDo giaTri={lyDo} onDoi={setLyDo} goiY={C.boLyDoGoiY} />
                  <div className="hang-chip" style={{ marginTop: 12 }}>
                    <button type="button" className="nut nut-vien" onClick={() => setHoi(null)}>
                      {C.thoi}
                    </button>
                    <button
                      type="button"
                      className="nut nut-nguy-dac"
                      style={{ flex: 1 }}
                      disabled={dang || lyDo.trim().length < 2}
                      onClick={() => void bo(k.id)}
                    >
                      {C.boNut}
                    </button>
                  </div>
                </li>
              )}
            </Fragment>
          ))}
        </ul>
      )}
    </section>
  )
}

/** Tờ "Ghi khoản chi": số tiền → nhóm → Lưu (tiền mặt, chi nhánh duy nhất là mặc định). */
/** Quầy chi tiền mặt tối đa (máy chủ `CHI_QUAY_TOI_DA_VND` giữ luật; ở đây chỉ báo trước). */
const CHI_QUAY_TOI_DA_VND = 2_000_000n
/** Lương / hoa hồng / ứng lương: chỉ chủ / quản lý ghi (máy chủ chặn). */
const NHOM_CHI_CHU: readonly NhomChiCoSan[] = ['luong-hoa-hong', 'ung-luong']

function ToGhiChi({
  api,
  choHoan,
  choThemNhom,
  onDong,
  onXong,
}: {
  api: Api
  choHoan: boolean
  choThemNhom: boolean
  onDong: () => void
  onXong: (cau: string) => void
}) {
  const [soTien, setSoTien] = useState('')
  const [nhom, setNhom] = useState<{ nhom: NhomChiCoSan | null; groupId: string | null } | null>(
    null,
  )
  const [rieng, setRieng] = useState<{ id: string; ten: string }[]>([])
  const [themNhom, setThemNhom] = useState<string | null>(null)
  const [cach, setCach] = useState<'cash' | 'transfer'>('cash')
  // ADR-101: ô chọn chi nhánh giữ nguyên (ghi hộ chi nhánh khác), mặc định = chi nhánh đang dùng.
  const { ds: cn, branchId, setBranchId } = useChiNhanhCuaTo()
  const [note, setNote] = useState('')
  const [dang, setDang] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  const [khoa, lamMoiKhoa] = useKhoaIdem()

  // Mở tờ là gõ số ngay (ui-critic v30).
  useEffect(() => {
    document.getElementById('chi-so-tien')?.focus()
  }, [])
  useEffect(() => {
    void api
      .nhomChi()
      .then((r) => setRieng(r.nhom))
      .catch(() => undefined)
  }, [api])

  const ung = nhom?.nhom === 'ung-luong'
  const chanNghin = /^\d+$/.test(soTien) && BigInt(soTien) > 0n && BigInt(soTien) % 1000n === 0n
  const quaTran =
    !choHoan && cach === 'cash' && /^\d+$/.test(soTien) && BigInt(soTien) > CHI_QUAY_TOI_DA_VND
  const hopLe =
    /^\d+$/.test(soTien) &&
    BigInt(soTien) > 0n &&
    BigInt(soTien) % 1000n === 0n &&
    nhom !== null &&
    branchId !== '' &&
    !quaTran &&
    (!ung || note.trim().length >= 2)

  async function luu() {
    if (!hopLe || nhom === null) return
    setDang(true)
    setLoi(null)
    try {
      await api.ghiKhoanChi(
        {
          nhom: nhom.nhom,
          groupId: nhom.groupId,
          soTienVnd: soTien,
          hinhThuc: cach,
          branchId,
          note: note.trim() === '' ? null : note.trim(),
        },
        khoa,
      )
      lamMoiKhoa()
      onXong(C.daGhi(tienVnd(soTien)))
    } catch (e) {
      setLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }

  async function luuNhom() {
    if (themNhom === null || themNhom.trim().length < 2) return
    setDang(true)
    setLoi(null)
    try {
      const g = await api.themNhomChi(themNhom.trim(), khoa)
      lamMoiKhoa()
      setRieng((r) => [...r, g])
      setNhom({ nhom: null, groupId: g.id })
      setThemNhom(null)
    } catch (e) {
      setLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }

  const chon = (n: NhomChiCoSan | null, g: string | null) =>
    nhom !== null && nhom.nhom === n && nhom.groupId === g
  return (
    <To nhan={C.ghi} onDong={onDong}>
      <header className="to-dau">
        <h2 style={{ flex: 1 }}>{C.ghi}</h2>
        <button
          type="button"
          className="nut nut-nhat nut-icon"
          aria-label={T.close}
          onClick={onDong}
        >
          <X size={22} />
        </button>
      </header>
      <div className="to-than">
        {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
        <label className="truong" htmlFor="chi-so-tien">
          <span className="nhan">{C.soTien}</span>
          <OTien id="chi-so-tien" giaTri={soTien} onDoi={setSoTien} />
        </label>
        {/* Nút Lưu tắt thì nói vì sao (ui-critic v30). */}
        {soTien !== '' && !chanNghin && <p className="chu-phu">{C.chanNghin}</p>}
        {chanNghin && nhom === null && <p className="chu-phu">{C.chonNhom}</p>}
        <div className="truong">
          <span className="nhan">{C.nhom}</span>
          <div className="hang-chip">
            {NHOM_CHI_CO_SAN.filter((n) => choHoan || !NHOM_CHI_CHU.includes(n)).map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={chon(n, null)}
                className={`chip ${chon(n, null) ? 'dang-chon' : ''}`}
                onClick={() => setNhom({ nhom: n, groupId: null })}
              >
                {NHOM_CHI_TEN[n]}
              </button>
            ))}
            {rieng.map((g) => (
              <button
                key={g.id}
                type="button"
                aria-pressed={chon(null, g.id)}
                className={`chip ${chon(null, g.id) ? 'dang-chon' : ''}`}
                onClick={() => setNhom({ nhom: null, groupId: g.id })}
              >
                {g.ten}
              </button>
            ))}
            {choThemNhom && themNhom === null && (
              <button type="button" className="chip" onClick={() => setThemNhom('')}>
                <Plus size={16} />
                {C.themNhom}
              </button>
            )}
          </div>
          {themNhom !== null && (
            <div className="hang-chip" style={{ marginTop: 8 }}>
              <input
                className="o-nhap"
                style={{ flex: 1 }}
                aria-label={C.tenNhomMoi}
                placeholder={C.tenNhomGoiY}
                maxLength={60}
                value={themNhom}
                onChange={(e) => setThemNhom(e.target.value)}
              />
              <button
                type="button"
                className="nut nut-vien"
                disabled={dang || themNhom.trim().length < 2}
                onClick={() => void luuNhom()}
              >
                {C.luuNhom}
              </button>
            </div>
          )}
        </div>
        <div className="truong">
          <span className="nhan">{C.cach}</span>
          <div className="hang-chip">
            {(['cash', 'transfer'] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={cach === m}
                className={`chip ${cach === m ? 'dang-chon' : ''}`}
                onClick={() => setCach(m)}
              >
                {m === 'cash' ? C.tienMat : C.chuyenKhoan}
              </button>
            ))}
          </div>
        </div>
        {cn.length > 1 && (
          <label className="truong" htmlFor="chi-chi-nhanh">
            <span className="nhan">{cach === 'cash' ? C.chiNhanh : C.chiNhanhCk}</span>
            <select
              id="chi-chi-nhanh"
              className="o-chon"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              {cn.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {quaTran && <ThongBao loai="canh-bao">{C.quaTranQuay}</ThongBao>}
        <label className="truong" htmlFor="chi-ghi-chu">
          <span className="nhan">{ung ? C.ungChoAi : C.ghiChu}</span>
          <input
            id="chi-ghi-chu"
            className="o-nhap"
            placeholder={ung ? C.ungGoiY : C.ghiChuGoiY}
            maxLength={300}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      </div>
      <footer className="to-chan">
        <button
          type="button"
          className="nut nut-chinh nut-lon"
          style={{ flex: 1 }}
          disabled={!hopLe || dang}
          onClick={() => void luu()}
        >
          {C.luu}
        </button>
      </footer>
    </To>
  )
}

/**
 * Thẻ Bảng hoa hồng, tháng đã chốt: ghi số đã trả thợ thành khoản chi (mỗi tháng một lần — máy chủ chặn lần hai).
 * Số mặc định = tổng bảng; thợ đã ứng lương thì sửa bớt (money-auditor 5.3 N2).
 */
export function NutHoaHongThang({
  api,
  thang,
  tongVnd,
  daGhiVnd,
}: {
  api: Api
  thang: string
  tongVnd: string
  daGhiVnd: string | null
}) {
  const [cach, setCach] = useState<'cash' | 'transfer' | null>(null)
  const [soTien, setSoTien] = useState(tongVnd)
  const { ds: cn, branchId, setBranchId } = useChiNhanhCuaTo()
  const [dang, setDang] = useState(false)
  const [daGhi, setDaGhi] = useState<string | null>(daGhiVnd)
  const [loi, setLoi] = useState<string | null>(null)
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const hopLe =
    /^\d+$/.test(soTien) &&
    BigInt(soTien) > 0n &&
    BigInt(soTien) <= BigInt(tongVnd) &&
    BigInt(soTien) % 1000n === 0n

  async function ghi() {
    if (cach === null || branchId === '' || !hopLe) return
    setDang(true)
    setLoi(null)
    try {
      const r = await api.ghiHoaHongThang(
        { thang, branchId, hinhThuc: cach, soTienVnd: soTien },
        khoa,
      )
      lamMoiKhoa()
      setDaGhi(r.soTienVnd)
    } catch (e) {
      setLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }

  if (daGhi !== null) {
    return (
      <p className="chu-phu" style={{ marginTop: 12 }}>
        {C.hoaHongDaCo(tienVnd(daGhi))}
      </p>
    )
  }
  return (
    <div className="muc-to" style={{ marginTop: 16 }}>
      <h3>{C.hoaHong(nhanThang(thang))}</h3>
      <p className="chu-phu">{C.hoaHongGiaiThich}</p>
      {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
      <label className="truong" htmlFor="hh-so-tien">
        <span className="nhan">{C.hoaHongSo}</span>
        <OTien id="hh-so-tien" giaTri={soTien} onDoi={setSoTien} />
      </label>
      <div className="truong">
        <span className="nhan">{C.cach}</span>
        <div className="hang-chip">
          {(['cash', 'transfer'] as const).map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={cach === m}
              className={`chip ${cach === m ? 'dang-chon' : ''}`}
              onClick={() => setCach(m)}
            >
              {m === 'cash' ? C.tienMat : C.chuyenKhoan}
            </button>
          ))}
        </div>
      </div>
      {/* Tiền mặt ra từ két nào — tiệm nhiều chi nhánh phải chọn đúng. */}
      {cach === 'cash' && cn.length > 1 && (
        <label className="truong" htmlFor="hh-chi-nhanh">
          <span className="nhan">{C.chiNhanh}</span>
          <select
            id="hh-chi-nhanh"
            className="o-chon"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
          >
            {cn.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <button
        type="button"
        className="nut nut-chinh nut-lon"
        style={{ width: '100%', marginTop: 12 }}
        disabled={cach === null || dang || branchId === '' || !hopLe}
        onClick={() => void ghi()}
      >
        {C.luu}
      </button>
    </div>
  )
}
